import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CanActivate, Router } from '@angular/router';
import { NetworkHelper } from '../helpers';
import { Observable, throwError } from 'rxjs';
import { StorageService } from './storage.service';
import { map, catchError } from 'rxjs/operators';
import { AUTH_ROUTES, SHARED_ENV } from '../tokens';

@Injectable({ providedIn: 'root' })
export class AuthService implements CanActivate {
  private readonly env = inject(SHARED_ENV);
  private readonly authRoutes = inject(AUTH_ROUTES);
  private readonly networkHelper = inject(NetworkHelper);
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);
  private readonly hostUrl: string;

  private isAuthenticated = true; // Set this value dynamically

  constructor() {
    this.hostUrl = this.env.host;
    this.isAuthenticated = this.storage.getItem('isLoggedIn') === 'true';
  }

  canActivate() {
    if (this.isAuthenticated) {
      return true;
    }

    this.router.navigateByUrl(this.authRoutes.loginUrl, { replaceUrl: true });
    return false;
  }

  register(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/register`, data, '');
  }

  agreeWithTerms(): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/accept-terms`, {});
  }

  verifyEmail(email: string, code: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/auth/activate`,
      { email, code },
      ''
    );
  }

  resendVerification(email: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/user/resend`,
      { email },
      ''
    );
  }

  get userEmail() {
    return this.storage.getItem('email');
  }

  get userRole() {
    return this.storage.getItem('role');
  }

  get userId() {
    return this.storage.getItem('userId');
  }

  get hasAgreedWithTerms(): boolean {
    return this.storage.getItem('hasAgreedWithTerms') === 'true';
  }

  googleAuth(idToken: string): Observable<any> {
    return this.networkHelper
      .post<any>(`${this.hostUrl}/auth/google-auth`, {
        idToken,
        isAgreeWithTerms: true,
      }, '')
      .pipe(
        map((result: any) => {
          this.isAuthenticated = true;
          return result;
        }),
        catchError((error) => throwError(() => error))
      );
  }

  appleAuth(idToken: string): Observable<any> {
    return this.networkHelper
      .post<any>(`${this.hostUrl}/auth/apple-auth`, {
        idToken,
        deviceType: 'web',
        isAgreeWithTerms: true,
      }, '')
      .pipe(
        map((result: any) => {
          this.isAuthenticated = true;
          return result;
        }),
        catchError((error) => throwError(() => error))
      );
  }

  login(email: string, password: string, captcha: string): Observable<any> {
    return this.networkHelper
      .post<any>(`${this.hostUrl}/auth/login`, {
        captcha: captcha,
        login: email,
        password: password,
      })
      .pipe(
        map((result: any) => {
          this.isAuthenticated = true;
          return result;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  logout(): Observable<any> {
    const onLoggedOut = () => {
      this.isAuthenticated = false;
      this.clearStorage();
      this.router.navigateByUrl(this.authRoutes.loginUrl, { replaceUrl: true });
    };

    return this.networkHelper.post(`${this.hostUrl}/auth/logout`, {}).pipe(
      map(() => onLoggedOut()),
      catchError((error) => {
        onLoggedOut();
        return throwError(() => error);
      })
    );
  }

  public setStorage(result: any, remember: boolean) {
    if (remember) {
      this.storage.setStorage(localStorage);
      localStorage.setItem('rememberMe', 'true');
    } else {
      this.storage.setStorage(sessionStorage);
      localStorage.setItem('rememberMe', 'false');
    }

    this.storage.setItem('token', result.token);
    this.storage.setItem('userId', result.userId);
    this.storage.setItem('isLoggedIn', 'true');

    if (result.email) this.storage.setItem('email', result.email);
    if (result.role) this.storage.setItem('role', result.role);
    if (result.tfa) this.storage.setItem('tfa', result.tfa);
    if (result.TFAEnabled != null)
      this.storage.setItem('TFAEnabled', String(result.TFAEnabled));
    if (result.firstLogin != null)
      this.storage.setItem('firstLogin', String(result.firstLogin));
    this.storage.setItem(
      'hasAgreedWithTerms',
      String(!!result.isAgreeWithTerms)
    );
    this.storage.setItem('TFAVerified', 'false');
  }

  /**
   * Exchange platform token for a GTN-specific token.
   * Mobile equivalent: AuthRepository.getGtnToken()
   * Endpoint: GET /auth/gtn/token (on main backend, authenticated with platform token)
   */
  fetchGtnToken(): Observable<any> {
    return this.networkHelper
      .get<any>(`${this.hostUrl}/auth/gtn/token`)
      .pipe(
        map((result: any) => {
          if (result?.accessToken) {
            this.storage.setItem('gtnToken', result.accessToken);
          }
          if (result?.refreshToken) {
            this.storage.setItem('gtnRefreshToken', result.refreshToken);
          }
          if (result?.accessTokenExpiresAt != null) {
            this.storage.setItem('gtnAccessTokenExpiresAt', String(result.accessTokenExpiresAt));
          }
          if (result?.refreshTokenExpiresAt != null) {
            this.storage.setItem('gtnRefreshTokenExpiresAt', String(result.refreshTokenExpiresAt));
          }
          return result;
        }),
        catchError((error) => {
          console.error('[AuthService] Failed to fetch GTN token:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Refresh the GTN token using GTN's own refresh endpoint.
   * Mobile equivalent: AuthenticationRepository.getGtnTokenByRefreshToken()
   * Endpoint: POST /trade/auth/customer/token/refresh (on GTN host)
   *
   * Uses HttpClient directly with X-Gtn-Retry header to prevent the
   * interceptor from attempting another refresh on a 401 from this call.
   */
  refreshGtnTokenViaGtn(): Observable<any> {
    const gtnHost = this.env.gtnHost;
    const refreshToken = this.storage.getItem('gtnRefreshToken');

    if (!gtnHost || !refreshToken) {
      return throwError(() => new Error('No GTN host or refresh token'));
    }

    const headers = new HttpHeaders({
      'X-Gtn-Retry': '1',
      ...(this.env.throttleKey ? { 'Throttle-Key': this.env.throttleKey } : {}),
    });

    return this.http
      .post<any>(
        `${gtnHost}/trade/auth/customer/token/refresh`,
        { refreshToken },
        { headers },
      )
      .pipe(
        map((result: any) => {
          const data = result?.result ?? result;
          if (data?.accessToken) {
            this.storage.setItem('gtnToken', data.accessToken);
          }
          if (data?.refreshToken) {
            this.storage.setItem('gtnRefreshToken', data.refreshToken);
          }
          if (data?.accessTokenExpiresAt != null) {
            this.storage.setItem('gtnAccessTokenExpiresAt', String(data.accessTokenExpiresAt));
          }
          if (data?.refreshTokenExpiresAt != null) {
            this.storage.setItem('gtnRefreshTokenExpiresAt', String(data.refreshTokenExpiresAt));
          }
          if (!data?.accessToken) {
            throw new Error('GTN refresh returned no access token');
          }
          return data;
        }),
        catchError((error) => {
          console.error('[AuthService] GTN native token refresh failed:', error);
          return throwError(() => error);
        }),
      );
  }

  public clearStorage() {
    this.storage.removeItem('token');
    this.storage.removeItem('gtnToken');
    this.storage.removeItem('gtnRefreshToken');
    this.storage.removeItem('gtnAccessTokenExpiresAt');
    this.storage.removeItem('gtnRefreshTokenExpiresAt');
    this.storage.removeItem('userId');
    this.storage.removeItem('role');
    this.storage.removeItem('email');
    this.storage.removeItem('isLoggedIn');
    this.storage.removeItem('TFAEnabled');
    this.storage.removeItem('TFAVerified');
    this.storage.removeItem('tfa');
    this.storage.removeItem('firstLogin');
    this.storage.removeItem('hasAgreedWithTerms');
    this.storage.removeItem('kycModalShown');
    this.storage.removeItem('questionnaireCompleted');
    this.storage.removeItem('kycStatus');
  }

  getSumsubAccessToken(): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/sumsub/accessToken`,
      { email: this.userEmail }
    );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/auth/change-password`, {
      oldPassword: oldPassword,
      newPassword: newPassword,
    });
  }

  forgotPassword(email: string, captcha?: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/auth/forgot`,
      { email, captcha },
      ''
    );
  }

  forgotResend(email: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/auth/forgot-resend`,
      { email },
      ''
    );
  }

  forgotConfirm(
    email: string,
    code: string,
    password: string
  ): Observable<any> {
    // code stays a string to preserve leading zeros
    return this.networkHelper.post(
      `${this.hostUrl}/auth/forgot-confirm`,
      { email, code, password },
      ''
    );
  }
}
